import { useMemo } from 'react';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
  show: boolean;
}

interface PasswordCriteria {
  label: string;
  met: boolean;
}

export const PasswordStrengthIndicator = ({ password, show }: PasswordStrengthIndicatorProps) => {
  const criteria = useMemo((): PasswordCriteria[] => {
    return [
      {
        label: 'Au moins 8 caractères',
        met: password.length >= 8
      },
      {
        label: 'Au moins une majuscule',
        met: /[A-Z]/.test(password)
      },
      {
        label: 'Au moins un chiffre',
        met: /[0-9]/.test(password)
      },
      {
        label: 'Au moins un caractère spécial',
        met: /[!@#$%^&*(),.?":{}|<>]/.test(password)
      }
    ];
  }, [password]);

  const strength = useMemo(() => {
    const metCount = criteria.filter(c => c.met).length;
    if (metCount === 0) return { label: '', color: '', width: '0%' };
    if (metCount <= 1) return { label: 'Faible', color: 'bg-red-500', width: '25%' };
    if (metCount === 2) return { label: 'Moyen', color: 'bg-orange-500', width: '50%' };
    if (metCount === 3) return { label: 'Bon', color: 'bg-yellow-500', width: '75%' };
    return { label: 'Fort', color: 'bg-green-500', width: '100%' };
  }, [criteria]);

  if (!show || !password) return null;

  return (
    <div className="mt-2 space-y-2">
      {/* Barre de progression */}
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-600">Force du mot de passe</span>
          {strength.label && (
            <span className={`text-xs font-medium ${
              strength.label === 'Faible' ? 'text-red-600' :
              strength.label === 'Moyen' ? 'text-orange-600' :
              strength.label === 'Bon' ? 'text-yellow-600' :
              'text-green-600'
            }`}>
              {strength.label}
            </span>
          )}
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${strength.color}`}
            style={{ width: strength.width }}
          />
        </div>
      </div>

      {/* Liste des critères */}
      <div className="space-y-1">
        {criteria.map((criterion, index) => (
          <div key={index} className="flex items-center gap-1.5">
            {criterion.met ? (
              <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
            ) : (
              <X className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            )}
            <span className={`text-xs ${criterion.met ? 'text-green-700' : 'text-gray-500'}`}>
              {criterion.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
